// BackController.jsx (핵심만 발췌)
import { useEffect, useRef } from "react";
import { useLocation, useNavigationType, useNavigate } from "react-router-dom";
import { addAppMessageListener, notifyNavState, postToApp } from "../bridges/appBridge";
import { useUIState } from "./uiStateContext";

export default function BackController({ rootPath = "/ads/main" }) {
    const location = useLocation();
    const navType = useNavigationType();
    const navigate = useNavigate();
    const {
        bottomSheetOpen, modalOpen, needsConfirm, hasBlockingUI,
        closeBottomSheet, closeModal, setNeedsConfirm, triggerPageBackHandler
    } = useUIState();

    const depthRef = useRef(0);
    const lastSentRef = useRef("");

    // ✅ 최신 상태를 담아둘 ref들 (리스너는 이 값만 읽음)
    const pathnameRef = useRef(location.pathname);
    const uiRef = useRef({
        bottomSheetOpen, modalOpen, needsConfirm, hasBlockingUI
    });

    useEffect(() => { pathnameRef.current = location.pathname; }, [location.pathname]);
    useEffect(() => {
        uiRef.current = { bottomSheetOpen, modalOpen, needsConfirm, hasBlockingUI };
    }, [bottomSheetOpen, modalOpen, needsConfirm, hasBlockingUI]);

    const normalize = (p) => (p || "/").replace(/\/+$/, "");
    const ALWAYS_BLOCK_PATHS = ["/ads/loading", "/ads/auto/loading"];
    const searchRef = useRef(location.search);
    useEffect(() => { searchRef.current = location.search; }, [location.search]);

    // const isAlwaysBlock = (pathname) =>
    //     ALWAYS_BLOCK_PATHS.some((p) => normalize(pathname).startsWith(normalize(p)));
    // 경로 + 쿼리 둘 다 확인 (/ads/register?step=0)
    const isAlwaysBlock = (pathname, search) => {
    const normalize = (p) => (p || "/").replace(/\/+$/, "");
    const p = normalize(pathname);

    if (ALWAYS_BLOCK_PATHS.some(base => p.startsWith(normalize(base)))) return true;
    return false;
    };

    const extraRootPrefixes = ["/ads/login"];
    const isRoot = (pathname, search) => {
        const p = normalize(pathname);
        if (p === normalize(rootPath)) return true;
        if (p === "/ads/register") {
            return true;
        }
        return extraRootPrefixes.some((prefix) => {
            const n = normalize(prefix);
            return p === n || p.startsWith(n + "/");
        });
    };

    const buildNavState = (atRoot) => {
        const { bottomSheetOpen, modalOpen, needsConfirm, hasBlockingUI } = uiRef.current;
        const hint = (bottomSheetOpen || modalOpen) ? "modal" : (atRoot ? "home" : "page");
        return {
            isRoot: atRoot,
            canGoBackInWeb: depthRef.current > 0,
            hasBlockingUI,
            needsConfirm,
            hint,
        };
    };

    // 앱 시작 직후 1회 NAV_STATE 송신 (그대로 OK)
    useEffect(() => {
        const atRoot = isRoot(pathnameRef.current, searchRef.current);
        const payload = buildNavState(atRoot);
        notifyNavState(payload);
        lastSentRef.current = JSON.stringify(payload);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 라우트 변화마다 깊이 갱신 + NAV_STATE 송신 (그대로 OK)
    useEffect(() => {
        const atRoot = isRoot(location.pathname, location.search);
        if (atRoot) {
            depthRef.current = 0;
        } else {
            if (navType === "PUSH") depthRef.current += 1;
            else if (navType === "POP") depthRef.current = Math.max(0, depthRef.current - 1);
        }

        const payload = buildNavState(atRoot);
        const key = JSON.stringify(payload);
        if (lastSentRef.current !== key) {
            notifyNavState(payload);
            lastSentRef.current = key;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, location.pathname, navType, bottomSheetOpen, modalOpen, hasBlockingUI, needsConfirm]);

    // ✅ BACK_REQUEST 리스너는 "마운트 1회만" 등록
    useEffect(() => {
        const off = addAppMessageListener((msg) => {
            if (msg?.type !== "BACK_REQUEST") return;

            const pathname = pathnameRef.current;
            const search = searchRef.current;
            const { bottomSheetOpen, modalOpen, needsConfirm } = uiRef.current;

            if (isAlwaysBlock(pathname, search)) {
                postToApp({ type: "BACK_HANDLED", by: "always-block" });
                return;
            }

            // 1) 바텀시트
            if (closeBottomSheet?.()) {
                postToApp({ type: "BACK_HANDLED", by: "bottomSheet" });
                return;
            }

            // 2) 모달
            if (modalOpen) {
                closeModal?.();
                postToApp({ type: "BACK_HANDLED", by: "modal" });
                return;
            }

            // 3) 페이지 핸들러
            if (triggerPageBackHandler?.()) {
                postToApp({ type: "BACK_HANDLED", by: "page-handler" });
                return;
            }

            // 4) 폼 확인
            if (needsConfirm) {
                const ok = window.confirm("변경사항이 저장되지 않을 수 있습니다. 그래도 이동하시겠습니까?");
                if (ok) {
                    postToApp({ type: "BACK_HANDLED", by: "confirm-ok" });
                    setNeedsConfirm?.(false);
                    if (depthRef.current > 0) navigate(-1);
                    else if (!isRoot(pathname, search)) {
                        navigate(rootPath, { replace: true });
                        depthRef.current = 0;
                    } else {
                        postToApp({ type: "EXIT_APP" });
                    }
                } else {
                    postToApp({ type: "BACK_HANDLED", by: "confirm-cancel" });
                }
                return;
            }

            // 5) 일반 뒤로
            if (!isRoot(pathname, search) && depthRef.current > 0) {
                navigate(-1);
                postToApp({ type: "BACK_HANDLED", by: "history" });
                return;
            }

            // 6) 루트 처리
            if (isRoot(pathname, search)) {
                const ok = window.confirm("앱을 종료하시겠습니까?");
                if (ok) postToApp({ type: "EXIT_APP" });
                else postToApp({ type: "BACK_HANDLED", by: "stay-on-root" });
                return;
            }

            // 7) 딥링크 앵커링
            navigate(rootPath, { replace: true });
            depthRef.current = 0;
            postToApp({ type: "BACK_HANDLED", by: "go-root" });
        });

        return () => off?.(); // ✅ 언마운트 시 해제
        // ⛔ 의존성 비움: 리스너는 한 번만 등록
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}
